/**
 * Button — vendored from shadcn/ui and restyled on import (style guide §7.1,
 * NFR-008). Variants map to the §7.1 table: primary, secondary, ghost,
 * destructive (§7.1 "danger"), danger-secondary and link. Weight 500, heights
 * 32 / 36 / 44.
 *
 * Disabled keeps its colors at opacity 0.45 and sets `aria-disabled`. Every
 * disabled button states its reason (§7.1, §11; mission 6 WP03 T021, post-plan
 * T10, post-tasks P11), and the props type enforces it: `disabled` is
 * `disabledReason` (rendered as a Tooltip and wired to `aria-describedby`), or
 * the caller names the element that states it (`aria-describedby`) or marks the
 * button busy (`aria-busy`). `loading` keeps the label, swaps the leading
 * `icon` for a spinner, sets `aria-busy` and ignores clicks. Focus uses the
 * global `--focus-ring` (§4.4) so the ring is identical to every other control.
 */
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from './cn'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors duration-[var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:cursor-not-allowed aria-disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-navy-950 text-white hover:bg-navy-800 active:bg-[var(--color-primary-active)]',
        secondary:
          'border border-navy-500 bg-white text-navy-950 hover:border-navy-600 hover:bg-navy-50 active:bg-navy-100',
        ghost: 'text-navy-700 hover:bg-navy-100 hover:text-navy-950 active:bg-navy-200',
        destructive:
          'bg-[var(--color-error-600)] text-white hover:bg-[var(--color-error-700)] active:bg-[var(--color-error-800)]',
        'danger-secondary':
          'border border-[var(--color-error-200)] bg-white text-[var(--color-error-700)] hover:bg-[var(--color-error-50)]',
        link: 'h-auto px-0 text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-body-sm',
        default: 'h-9 px-4 text-body',
        lg: 'h-11 px-5 text-[16px] leading-6',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

type BaseProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> &
  VariantProps<typeof buttonVariants> & {
    /** Render the single child element instead of a `<button>` (e.g. a router link). */
    asChild?: boolean
    /** Busy: the label stays, a spinner replaces `icon`, `aria-busy` is set and clicks are ignored (§7.1). */
    loading?: boolean
    /** The leading icon (16px); replaced by the spinner while `loading`. */
    icon?: React.ReactNode
  }

// mission 6 WP13: a stated reason is never nullish or a boolean (WP03 review N1); an empty or blank string is
// reported by assertDisabledReason in development, since the type cannot exclude it from `string`.
export type DisabledReason = Exclude<React.ReactNode, null | undefined | boolean>

/**
 * Development only: reports, as React's own dev warnings do, a disabled control whose reason is empty (§7.1,
 * §11; N1). It logs rather than throws, so a copy gap never takes a screen down while developing.
 */
export function assertDisabledReason(component: string, reason: unknown): void {
  if (!import.meta.env.DEV) return
  const empty = reason === undefined || reason === null || typeof reason === 'boolean' || (typeof reason === 'string' && reason.trim() === '')
  if (empty) console.error(`${component}: a disabled control states its reason; disabledReason is empty (style guide §7.1, N1)`)
}

type Enabled = { disabled?: false; disabledReason?: undefined }
type Reasoned = { disabled: true; disabledReason: DisabledReason }
/** The caller states the reason inline and wires it (AuthLayout `DisabledReason`), or the button is busy. */
type CallerStated =
  | { disabled: boolean; disabledReason?: undefined; 'aria-describedby': string | undefined }
  | { disabled: boolean; disabledReason?: undefined; 'aria-busy': boolean | undefined }

export type ButtonProps = BaseProps & (Enabled | Reasoned | CallerStated)

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      disabled,
      disabledReason,
      loading = false,
      icon,
      type,
      onClick,
      children,
      ...props
    },
    ref,
  ) => {
    const reasonId = React.useId()
    // mission 6 WP13: a disabled button that is not caller-stated must carry a non-empty reason (N1).
    if (disabled === true && !('aria-describedby' in props) && !('aria-busy' in props)) assertDisabledReason('Button', disabledReason)
    const hasReason = disabled === true && disabledReason !== undefined
    const Comp = asChild ? Slot : 'button'
    const describedBy = [props['aria-describedby'], hasReason ? reasonId : undefined].filter(Boolean).join(' ')
    const button = (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled}
        aria-disabled={disabled ? true : undefined}
        // A button never submits a form by accident; forms opt in with type="submit".
        type={asChild ? type : (type ?? 'button')}
        {...props}
        aria-busy={loading ? true : props['aria-busy']}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        onClick={loading ? (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault() : onClick}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 aria-hidden className="animate-spin" /> : icon}
            {children}
          </>
        )}
      </Comp>
    )
    if (!hasReason) return button
    // A disabled <button> fires no pointer events: the focusable span carries
    // the tooltip, and the reason is also in the button's description.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]">
            {button}
            <span id={reasonId} className="sr-only">
              {disabledReason}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{disabledReason}</TooltipContent>
      </Tooltip>
    )
  },
)
Button.displayName = 'Button'
